import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { firestore } from "../firebase";
import { fetchSoldOutItems } from "../api";
import {
  loadRazorpayScript,
  createOrderOnBackend,
  openRazorpayCheckout,
  verifyPaymentOnBackend,
} from "../services/razorpay";
import {
  AVAILABILITY_ERROR_CODES,
  findInvalidCartItems,
  hasInvalidCartItems,
} from "../utils/cartAvailability";
import { buildRistaQrOrderLabel } from "../utils/ristaOrderLabel";
import { getTableNumberFromUrl } from "../utils/tableNumber";
import { hasBlockedCartSelection } from "../utils/menuSkuPolicy";

/**
 * CartContext
 * -----------
 * Owns the cart as the single source of truth for the online menu ordering
 * flow. The cart is persisted to Firestore (`menu_carts/{sessionId}__{cafeCode}`)
 * with realtime sync so a guest can resume on another device, while a
 * localStorage copy keeps it fast/offline-friendly.
 *
 * Checkout is performed via the gottea-api backend:
 *   create order → open Razorpay checkout → verify signature → start a fresh cart.
 */

const CartContext = createContext(null);

const SESSION_STORAGE_KEY = "gottea-cart-session";
const LEGACY_CART_PREFIX = "gottea-cart"; // pre-redesign per-cafe localStorage key
const RISTA_CHANNELS = {
  DINE_IN: "Dine In GotTea",
  TAKEAWAY: "Takeaway GotTea",
};
const RISTA_SALE_CALLBACK_URL =
  "https://gottea-search-5gtrn.ondigitalocean.app/sales/rista/webhook";
const CART_TTL_DAYS = 7;
const CART_STATUSES = {
  CART: "cart",
  PAYMENT_STARTED: "payment_started",
  PAYMENT_VERIFIED: "payment_verified",
  RISTA_PENDING: "rista_pending",
  RISTA_SUCCESS: "rista_success",
  RISTA_FAILED: "rista_failed",
  PAYMENT_FAILED: "payment_failed",
};
const FINAL_CART_STATUSES = new Set([
  CART_STATUSES.RISTA_SUCCESS,
  CART_STATUSES.RISTA_FAILED,
  CART_STATUSES.PAYMENT_FAILED,
]);
const EMPTY_TOTALS = {
  subtotal: 0,
  taxTotal: 0,
  taxAmountIncluded: 0,
  taxAmountExcluded: 0,
  grandTotal: 0,
  itemCount: 0,
};

// ---- helpers --------------------------------------------------------------

const createSessionId = () =>
  (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
  `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const persistSessionId = (id) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    /* ignore storage errors */
  }
};

const syncExistingSessionQueryParams = (sessionId, cafeCode) => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let changed = false;

    if (url.searchParams.has("sessionId")) {
      url.searchParams.set("sessionId", sessionId);
      changed = true;
    }
    if (url.searchParams.has("cartId")) {
      url.searchParams.set("cartId", `${sessionId}__${cafeCode}`);
      changed = true;
    }

    if (changed) {
      window.history.replaceState({}, "", url.toString());
    }
  } catch {
    /* ignore URL update errors */
  }
};

const getOrCreateSessionId = () => {
  if (typeof window === "undefined") return "anon";
  try {
    const params = new URLSearchParams(window.location.search);
    const cartIdFromUrl = params.get("cartId");
    const sessionFromUrl =
      params.get("sessionId") ||
      (cartIdFromUrl && cartIdFromUrl.includes("__")
        ? cartIdFromUrl.split("__")[0]
        : cartIdFromUrl);

    if (sessionFromUrl) {
      const id = sessionFromUrl.trim();
      if (id) {
        persistSessionId(id);
        return id;
      }
    }

    let id = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = createSessionId();
      persistSessionId(id);
    }
    return id;
  } catch {
    return "anon";
  }
};

const cartDocId = (sessionId, cafeCode) => `${sessionId}__${cafeCode}`;

const legacyStorageKey = (cafeCode) => `${LEGACY_CART_PREFIX}-${cafeCode}`;

const getInitialTableNumber = () => getTableNumberFromUrl();

const tableLabelFromNumber = (tableNumber) => {
  const normalized = String(tableNumber || "").trim();
  return normalized ? `Table ${normalized}` : "";
};

const getRistaChannelForOrderType = (orderType) => {
  const normalizedOrderType = orderType === "takeaway" ? "takeaway" : "dine-in";
  return normalizedOrderType === "takeaway" ? RISTA_CHANNELS.TAKEAWAY : RISTA_CHANNELS.DINE_IN;
};

const isFinalCartStatus = (status) => FINAL_CART_STATUSES.has(status);

const timestampToMillis = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") {
    return value.seconds * 1000 + Math.round((value.nanoseconds || 0) / 1000000);
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const stripUndefined = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, val]) => {
      if (val !== undefined) {
        acc[key] = stripUndefined(val);
      }
      return acc;
    }, {});
  }
  return value;
};

const buildSaleTaxSummary = (saleItems) => {
  const summaries = new Map();
  (Array.isArray(saleItems) ? saleItems : []).forEach((item) => {
    (Array.isArray(item.taxes) ? item.taxes : []).forEach((tax) => {
      const name = tax.name || "Tax";
      const percentage = Number(tax.percentage || 0);
      const key = `${name}__${percentage}`;
      const current = summaries.get(key) || {
        name,
        percentage,
        saleAmount: 0,
        itemTaxIncluded: 0,
        itemTaxExcluded: 0,
        chargeTaxIncluded: 0,
        chargeTaxExcluded: 0,
        amountIncluded: 0,
        amountExcluded: 0,
        amount: 0,
      };
      const amountIncluded = Number(tax.amountIncluded || 0);
      const amountExcluded = Number(tax.amountExcluded || 0);
      current.saleAmount += Number(tax.saleAmount || 0);
      current.itemTaxIncluded += amountIncluded;
      current.itemTaxExcluded += amountExcluded;
      current.amountIncluded += amountIncluded;
      current.amountExcluded += amountExcluded;
      current.amount += Number(tax.amount ?? amountIncluded + amountExcluded);
      summaries.set(key, current);
    });
  });

  return Array.from(summaries.values()).map((tax) =>
    stripUndefined({
      name: tax.name,
      percentage: roundMoney(tax.percentage),
      saleAmount: roundMoney(tax.saleAmount),
      itemTaxIncluded: roundMoney(tax.itemTaxIncluded),
      itemTaxExcluded: roundMoney(tax.itemTaxExcluded),
      chargeTaxIncluded: roundMoney(tax.chargeTaxIncluded),
      chargeTaxExcluded: roundMoney(tax.chargeTaxExcluded),
      amountIncluded: roundMoney(tax.amountIncluded),
      amountExcluded: roundMoney(tax.amountExcluded),
      amount: roundMoney(tax.amount),
    })
  );
};

const createCartItemId = (product, variant = null, selectedOptions = []) => {
  const baseId = product?.id || product?.skuCode || product?.name || "item";
  const variantId = variant?.id || variant?.skuCode || "default";
  const optionIds = (Array.isArray(selectedOptions) ? selectedOptions : [])
    .map((option) => option.id)
    .filter(Boolean)
    .sort()
    .join(",");
  return `${baseId}::${variantId}::${optionIds}`;
};

const itemsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const normalizeSelectedOption = (option) => {
  const unitPrice = roundMoney(option?.unitPrice ?? option?.price ?? 0);
  return {
    id: option?.id || option?.optionId || option?.skuCode || option?.title,
    optionId: option?.optionId || option?.id || "",
    itemId: option?.itemId || "",
    skuCode: option?.skuCode || option?.id || "",
    name: option?.name || option?.title || "",
    title: option?.title || option?.name || "",
    groupId: option?.groupId || "",
    groupName: option?.groupName || "",
    price: unitPrice,
    unitPrice,
    quantity: 1,
  };
};

const buildCartItem = (product, variant = null, selectedOptions = [], quantity = 1) => {
  if (!product || hasBlockedCartSelection(product, variant, selectedOptions)) return null;

  const optionList = (Array.isArray(selectedOptions) ? selectedOptions : []).map(
    normalizeSelectedOption
  );
  const optionsExtra = optionList.reduce(
    (sum, o) => sum + Number(o?.unitPrice ?? o?.price ?? 0),
    0
  );
  const baseUnitPriceRaw = variant?.price ?? product?.price ?? 0;
  const baseUnitPrice = Number.isFinite(Number(baseUnitPriceRaw))
    ? Number(baseUnitPriceRaw)
    : 0;
  const unitPrice = roundMoney(baseUnitPrice + optionsExtra);
  const id = createCartItemId(product, variant, optionList);
  const variantTitle = variant?.title || "";
  const name = variantTitle ? `${product.name} (${variantTitle})` : product.name;
  const skuCode = variant?.skuCode || product?.skuCode || "";
  const itemId = variant?.id || product?.id || product?.skuCode || product?.name;
  const imageUrl = product?.imageUrl || product?.productDefaultImage || "";
  const categoryName = product?.categoryName || product?.parentName || "";
  const subCategoryName = product?.subCategoryName || "";
  const taxTypeIds = Array.isArray(variant?.taxTypeIds)
    ? variant.taxTypeIds
    : Array.isArray(product?.taxTypeIds)
    ? product.taxTypeIds
    : [];
  const isPriceIncludesTax = Boolean(
    variant?.isPriceIncludesTax ?? product?.isPriceIncludesTax
  );

  return {
    id,
    itemId,
    productId: product?.id || product?.skuCode || product?.name,
    skuCode,
    name,
    shortName: product?.name || name,
    longName: name,
    productName: product?.name || "",
    categoryName,
    subCategoryName,
    variantTitle,
    selectedOptions: optionList,
    quantity,
    baseUnitPrice,
    optionTotal: roundMoney(optionsExtra),
    unitPrice,
    taxTypeIds,
    isPriceIncludesTax,
    imageUrl,
  };
};

const buildRistaSaleDraft = ({
  cafeCode,
  cartId,
  orderRef,
  items,
  totals,
  customer,
  orderType,
  tableNumber,
  payment,
}) => {
  const saleItems = (Array.isArray(items) ? items : []).map((item) => {
    const selectedOptions = Array.isArray(item.selectedOptions)
      ? item.selectedOptions
      : [];
    const optionAmount = roundMoney(
      selectedOptions.reduce(
        (sum, option) =>
          sum + Number(option?.unitPrice ?? option?.price ?? 0) * Number(item.quantity || 0),
        0
      )
    );
    const baseUnitPrice = roundMoney(
      item.baseUnitPrice ??
        Number(item.unitPrice || 0) -
          selectedOptions.reduce(
            (sum, option) => sum + Number(option?.unitPrice ?? option?.price ?? 0),
            0
          )
    );
    const itemAmount = roundMoney(baseUnitPrice * Number(item.quantity || 0));
    const itemTotalAmount = roundMoney(itemAmount + optionAmount);
    const taxAmountIncluded = roundMoney(item.taxAmountIncluded || 0);
    const taxAmountExcluded = roundMoney(item.taxAmountExcluded || 0);
    const taxAmount = roundMoney(item.taxAmount ?? taxAmountIncluded + taxAmountExcluded);

    return stripUndefined({
      shortName: item.shortName || item.productName || item.name,
      longName: item.longName || item.productName || item.name,
      categoryName: item.categoryName || undefined,
      variants: item.variantTitle || undefined,
      skuCode: item.skuCode || item.itemId || item.productId,
      quantity: Number(item.quantity || 0),
      unitPrice: baseUnitPrice,
      itemAmount,
      optionAmount,
      itemTotalAmount,
      grossAmount: itemTotalAmount,
      netDiscountAmount: 0,
      netAmount: itemTotalAmount,
      taxAmountIncluded,
      taxAmountExcluded,
      taxAmount,
      baseTaxAmount: taxAmount,
      options: selectedOptions.length
        ? selectedOptions.map((option) =>
            stripUndefined({
              type: "Option",
              name: option.name || option.title,
              itemName: option.name || option.title,
              optionId: option.optionId || option.id,
              skuCode: option.skuCode || option.optionId || option.id,
              quantity: Number(item.quantity || 0),
              unitPrice: roundMoney(option.unitPrice ?? option.price ?? 0),
              amount: roundMoney(
                Number(option.unitPrice ?? option.price ?? 0) * Number(item.quantity || 0)
              ),
              taxAmount: 0,
              taxes: [],
            })
          )
        : undefined,
      discounts: [],
      taxes: Array.isArray(item.taxes) ? item.taxes : [],
    });
  });

  const normalizedOrderType = customer?.orderType || orderType;
  const channel = getRistaChannelForOrderType(normalizedOrderType);
  const saleTaxes = buildSaleTaxSummary(saleItems);
  const normalizedTableNumber = String(customer?.tableNumber || tableNumber || "").trim();
  const orderLabel = buildRistaQrOrderLabel({
    orderType: normalizedOrderType,
    tableNumber: normalizedTableNumber,
  });
  const note = String(customer?.notes || "").trim().slice(0, 250);

  return stripUndefined({
    branchCode: cafeCode,
    status: "Closed",
    sourceInfo: {
      companyName: "Got Tea",
      invoiceNumber: orderRef,
      orderTransactionId: cartId,
      callbackURL: RISTA_SALE_CALLBACK_URL,
      source: "gottea-menu",
      isEcomOrder: true,
      appUserId: customer?.appUserId || undefined,
      customerRegistrationStatus: customer?.registrationStatus || undefined,
      isRegisteredAppUser: customer?.isRegisteredAppUser === true ? true : undefined,
      estimatedMissedTokens: customer?.estimatedMissedTokens || undefined,
    },
    channel,
    label: orderLabel || undefined,
    customer: customer
      ? {
          name: customer.name,
          phoneNumber: customer.phone,
          appUserId: customer.appUserId || undefined,
          registrationStatus: customer.registrationStatus || undefined,
          isRegisteredAppUser: customer.isRegisteredAppUser === true ? true : undefined,
        }
      : undefined,
    items: saleItems,
    itemTotalAmount: roundMoney(totals?.subtotal || 0),
    taxAmountIncluded: roundMoney(totals?.taxAmountIncluded || 0),
    taxAmountExcluded: roundMoney(totals?.taxAmountExcluded ?? totals?.taxTotal ?? 0),
    taxAmount: roundMoney(
      totals?.taxAmount ?? (totals?.taxAmountIncluded || 0) + (totals?.taxAmountExcluded ?? totals?.taxTotal ?? 0)
    ),
    billAmount: roundMoney(totals?.grandTotal || 0),
    grossAmount: roundMoney(totals?.subtotal || 0),
    netDiscountAmount: 0,
    netAmount: roundMoney(totals?.subtotal || 0),
    totalAmount: roundMoney(totals?.grandTotal || 0),
    discounts: [],
    taxes: saleTaxes,
    note: note || undefined,
    payments: payment?.razorpayPaymentId
      ? [
          {
            mode: "Razorpay",
            amount: roundMoney((payment.amountPaise || 0) / 100),
            reference: payment.razorpayPaymentId,
            note: payment.razorpayOrderId || payment.orderRef || orderRef,
          },
        ]
      : undefined,
    balanceAmount: payment?.razorpayPaymentId ? 0 : roundMoney(totals?.grandTotal || 0),
  });
};

// ---- provider -------------------------------------------------------------

export function CartProvider({ cafeCode, children }) {
  const [activeSessionId, setActiveSessionId] = useState(() => getOrCreateSessionId());
  const [items, setItems] = useState([]);
  const [taxRatesById, setTaxRatesById] = useState({});
  const [cafeInfo, setCafeInfo] = useState({ branchName: "", branchPhone: "" });
  const [hydrated, setHydrated] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [cartStatus, setCartStatus] = useState(CART_STATUSES.CART);
  const [invalidCartItems, setInvalidCartItems] = useState({});
  const [cartAvailabilityError, setCartAvailabilityError] = useState("");
  const [customerInfo, setCustomerInfo] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [ristaInfo, setRistaInfo] = useState(null);
  const [orderContext, setOrderContext] = useState(() => {
    const tableNumber = getInitialTableNumber();
    return {
      orderType: "dine-in",
      tableNumber,
      tableLabel: tableLabelFromNumber(tableNumber),
      notes: "",
    };
  });

  // Refs to manage Firestore sync without feedback loops.
  const sessionIdRef = useRef(activeSessionId);
  const tableNumberRef = useRef(getInitialTableNumber());
  const createdAtRef = useRef(null);
  const hydratedRef = useRef(false);
  const lastWriteAtRef = useRef(0);
  const localItemsMutationAtRef = useRef(0);
  const latestItemsRef = useRef([]);
  const applyingRemoteRef = useRef(false);
  const writeTimerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const cafeCodeRef = useRef(cafeCode);

  useEffect(() => {
    latestItemsRef.current = items;
  }, [items]);

  useEffect(() => {
    sessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  // Keep the latest cafeCode in a ref for use inside stable callbacks/effects.
  useEffect(() => {
    cafeCodeRef.current = cafeCode;
  }, [cafeCode]);

  useEffect(() => {
    const itemIds = new Set(items.map((item) => item.id));

    setInvalidCartItems((prev) => {
      const next = Object.keys(prev || {}).reduce((acc, id) => {
        if (itemIds.has(id)) {
          acc[id] = prev[id];
        }
        return acc;
      }, {});

      return itemsEqual(prev, next) ? prev : next;
    });

    if (items.length === 0) {
      setCartAvailabilityError("");
    }
  }, [items]);

  const persistLocal = useCallback((next, code) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(legacyStorageKey(code), JSON.stringify(next));
    } catch {
      /* ignore quota errors */
    }
  }, []);

  const writeCartDocument = useCallback(async (doc) => {
    try {
      const updatedAt = Date.now();
      lastWriteAtRef.current = updatedAt;
      const code = doc.cafeCode || cafeCodeRef.current;
      const cartId = doc.cartId || cartDocId(sessionIdRef.current, code);
      const createdAt = createdAtRef.current || doc.createdAt || updatedAt;
      createdAtRef.current = createdAt;

      await firestore
        .collection("menu_carts")
        .doc(cartId)
        .set(
          stripUndefined({
            ...doc,
            cartId,
            sessionId: sessionIdRef.current,
            cafeCode: code,
            channel: doc.channel || RISTA_CHANNELS.DINE_IN,
            createdAt,
            updatedAt,
            updatedAtClient: new Date().toISOString(),
            expiresAt:
              doc.expiresAt ||
              new Date(Date.now() + CART_TTL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
          }),
          { merge: true }
        );
      setSyncError(false);
    } catch (error) {
      // Firestore rules / network failure — keep working off localStorage.
      console.warn("[cart] Firestore write failed, using localStorage only:", error);
      setSyncError(true);
    }
  }, []);

  const applyCartDocumentMeta = useCallback((data) => {
    if (!data) return;
    if (data.createdAt) createdAtRef.current = data.createdAt;
    if (data.status) setCartStatus(data.status);
    if (data.customer !== undefined) setCustomerInfo(data.customer || null);
    if (data.payment !== undefined) setPaymentInfo(data.payment || null);
    if (data.rista !== undefined) setRistaInfo(data.rista || null);
    setOrderContext((prev) => ({
      ...prev,
      orderType: data.orderType || prev.orderType,
      tableNumber:
        data.tableNumber !== undefined
          ? data.tableNumber || tableNumberRef.current || ""
          : prev.tableNumber,
      tableLabel:
        data.tableLabel !== undefined
          ? data.tableLabel || tableLabelFromNumber(data.tableNumber || tableNumberRef.current)
          : prev.tableLabel,
      notes: data.notes !== undefined ? data.notes || "" : prev.notes,
    }));
  }, []);

  // (Re)hydrate + subscribe whenever the cafe changes.
  useEffect(() => {
    const code = cafeCode;
    const sessionId = activeSessionId;
    sessionIdRef.current = sessionId;

    if (!code) {
      setItems([]);
      setHydrated(false);
      setCartStatus(CART_STATUSES.CART);
      setCustomerInfo(null);
      setPaymentInfo(null);
      setRistaInfo(null);
      hydratedRef.current = false;
      return;
    }

    // Reset for the new cafe.
    setItems([]);
    setHydrated(false);
    setCartStatus(CART_STATUSES.CART);
    setCustomerInfo(null);
    setPaymentInfo(null);
    setRistaInfo(null);
    hydratedRef.current = false;
    lastWriteAtRef.current = 0;
    localItemsMutationAtRef.current = 0;
    applyingRemoteRef.current = false;
    createdAtRef.current = null;

    // Tear down any previous listener.
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const docRef = firestore
      .collection("menu_carts")
      .doc(cartDocId(sessionId, code));

    let firstSnapshot = true;

    const hydrateFromLocal = () => {
      try {
        const raw = localStorage.getItem(legacyStorageKey(code));
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            setItems(parsed);
          }
        }
      } catch {
        /* ignore */
      }
    };

    const unsubscribe = docRef.onSnapshot(
      (doc) => {
        const data = doc.exists ? doc.data() : null;

        if (firstSnapshot) {
          firstSnapshot = false;
          if (localItemsMutationAtRef.current > 0) {
            hydratedRef.current = true;
            setHydrated(true);
            return;
          }
          if (data && Array.isArray(data.items)) {
            setItems(data.items);
            applyCartDocumentMeta(data);
            persistLocal(data.items, code);
          } else {
            hydrateFromLocal();
          }
          hydratedRef.current = true;
          setHydrated(true);
          return;
        }

        // Subsequent snapshots: only apply remote changes newer than our last
        // local write (i.e. another device/tab updated the cart).
        const remoteUpdatedAt = timestampToMillis(data?.updatedAt);
        const remoteItems = Array.isArray(data?.items) ? data.items : [];
        const remoteStatus = data?.status || data?.rista?.status || "";
        const remoteItemsAreStale =
          localItemsMutationAtRef.current > 0 && remoteUpdatedAt < localItemsMutationAtRef.current;
        const shouldApplyRemoteMeta =
          remoteUpdatedAt > lastWriteAtRef.current ||
          (isFinalCartStatus(remoteStatus) && !remoteItemsAreStale);

        if (shouldApplyRemoteMeta) {
          applyCartDocumentMeta(data);
        }

        if (
          shouldApplyRemoteMeta &&
          !remoteItemsAreStale &&
          !itemsEqual(remoteItems, latestItemsRef.current)
        ) {
          applyingRemoteRef.current = true;
          setItems(remoteItems);
          persistLocal(remoteItems, code);
        }
      },
      (error) => {
        // Permissions/offline — fall back to localStorage, keep UI usable.
        console.warn("[cart] Firestore subscription error:", error);
        setSyncError(true);
        if (!hydratedRef.current) {
          hydrateFromLocal();
          hydratedRef.current = true;
          setHydrated(true);
        }
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      unsubscribe();
      if (writeTimerRef.current) {
        clearTimeout(writeTimerRef.current);
        writeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [cafeCode, activeSessionId, persistLocal, applyCartDocumentMeta]);

  // ---- tax rates + cafe info (pushed in by the menu page) -----------------

  const setTaxRates = useCallback((map) => setTaxRatesById(map || {}), []);
  const setCafeDetails = useCallback(
    (info) => setCafeInfo((prev) => ({ ...prev, ...info })),
    []
  );

  // ---- cart mutations -----------------------------------------------------

  const markLocalItemsMutation = useCallback(() => {
    const now = Date.now();
    localItemsMutationAtRef.current = now;
    lastWriteAtRef.current = Math.max(lastWriteAtRef.current, now);
  }, []);

  const setOrderNotes = useCallback((notes) => {
    markLocalItemsMutation();
    setOrderContext((prev) => ({
      ...prev,
      notes: String(notes || "").slice(0, 250),
    }));
  }, [markLocalItemsMutation]);

  const resetCheckoutStateForCartEdit = useCallback(() => {
    setCartStatus(CART_STATUSES.CART);
    setCustomerInfo(null);
    setPaymentInfo(null);
    setRistaInfo(null);
    setCartAvailabilityError("");
  }, []);

  const clearCartAvailabilityError = useCallback(() => {
    setCartAvailabilityError("");
  }, []);

  const addToCart = useCallback((product, variant = null, selectedOptions = [], quantity = 1) => {
    if (!product || hasBlockedCartSelection(product, variant, selectedOptions)) return;
    markLocalItemsMutation();
    resetCheckoutStateForCartEdit();
    const addQty = Math.max(1, Math.floor(Number(quantity) || 1));

    const optionList = (Array.isArray(selectedOptions) ? selectedOptions : []).map(
      normalizeSelectedOption
    );
    const optionsExtra = optionList.reduce(
      (sum, o) => sum + Number(o?.unitPrice ?? o?.price ?? 0),
      0
    );
    const baseUnitPriceRaw = variant?.price ?? product?.price ?? 0;
    const baseUnitPrice = Number.isFinite(Number(baseUnitPriceRaw))
      ? Number(baseUnitPriceRaw)
      : 0;
    const unitPrice = roundMoney(baseUnitPrice + optionsExtra);
    const id = createCartItemId(product, variant, optionList);
    const variantTitle = variant?.title || "";
    const name = variantTitle ? `${product.name} (${variantTitle})` : product.name;
    const skuCode = variant?.skuCode || product?.skuCode || "";
    const itemId = variant?.id || product?.id || product?.skuCode || product?.name;
    const imageUrl = product?.imageUrl || product?.productDefaultImage || "";
    const categoryName = product?.categoryName || product?.parentName || "";
    const subCategoryName = product?.subCategoryName || "";
    const taxTypeIds = Array.isArray(variant?.taxTypeIds)
      ? variant.taxTypeIds
      : Array.isArray(product?.taxTypeIds)
      ? product.taxTypeIds
      : [];
    const isPriceIncludesTax = Boolean(
      variant?.isPriceIncludesTax ?? product?.isPriceIncludesTax
    );

    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      if (idx !== -1) {
        return prev.map((it, i) =>
          i === idx ? { ...it, quantity: it.quantity + addQty } : it
        );
      }
      return [
        ...prev,
        {
          id,
          itemId,
          productId: product?.id || product?.skuCode || product?.name,
          skuCode,
          name,
          shortName: product?.name || name,
          longName: name,
          productName: product?.name || "",
          categoryName,
          subCategoryName,
          variantTitle,
          selectedOptions: optionList,
          quantity: addQty,
          baseUnitPrice,
          optionTotal: roundMoney(optionsExtra),
          unitPrice,
          taxTypeIds,
          isPriceIncludesTax,
          imageUrl,
        },
      ];
    });
  }, [markLocalItemsMutation, resetCheckoutStateForCartEdit]);

  const updateQuantity = useCallback((itemId, delta) => {
    markLocalItemsMutation();
    resetCheckoutStateForCartEdit();
    setItems((prev) =>
      prev
        .map((it) => {
          if (it.id !== itemId) return it;
          if (delta > 0 && hasBlockedCartSelection(it, null, it.selectedOptions)) return it;
          return { ...it, quantity: it.quantity + delta };
        })
        .filter((it) => it.quantity > 0)
    );
  }, [markLocalItemsMutation, resetCheckoutStateForCartEdit]);

  const replaceCartItem = useCallback((originalItemId, product, variant = null, selectedOptions = [], quantity = 1) => {
    if (!originalItemId || !product) return;

    const nextQty = Math.max(1, Math.floor(Number(quantity) || 1));
    const replacement = buildCartItem(product, variant, selectedOptions, nextQty);
    if (!replacement) return;

    markLocalItemsMutation();
    resetCheckoutStateForCartEdit();
    setItems((prev) => {
      const original = prev.find((it) => it.id === originalItemId);
      if (!original) return prev;

      if (replacement.id === originalItemId) {
        return prev.map((it) => (it.id === originalItemId ? replacement : it));
      }

      const existingTarget = prev.find((it) => it.id === replacement.id);
      if (existingTarget) {
        return prev
          .filter((it) => it.id !== originalItemId)
          .map((it) =>
            it.id === replacement.id
              ? { ...replacement, quantity: Number(it.quantity || 0) + nextQty }
              : it
          );
      }

      return prev.map((it) => (it.id === originalItemId ? replacement : it));
    });
  }, [markLocalItemsMutation, resetCheckoutStateForCartEdit]);

  const removeItem = useCallback((itemId) => {
    markLocalItemsMutation();
    resetCheckoutStateForCartEdit();
    setItems((prev) => prev.filter((it) => it.id !== itemId));
  }, [markLocalItemsMutation, resetCheckoutStateForCartEdit]);

  const clearCart = useCallback(() => {
    markLocalItemsMutation();
    setItems([]);
    setCartStatus(CART_STATUSES.CART);
    setInvalidCartItems({});
    setCartAvailabilityError("");
    setCustomerInfo(null);
    setPaymentInfo(null);
    setRistaInfo(null);
  }, [markLocalItemsMutation]);

  const startNewCartAfterSuccessfulOrder = useCallback(async () => {
    const code = cafeCodeRef.current;
    if (!code) return;
    const nextSessionId = createSessionId();
    const tableNumber = String(orderContext.tableNumber || tableNumberRef.current || "").trim();
    const nextOrderContext = {
      orderType: orderContext.orderType || "dine-in",
      tableNumber,
      tableLabel: tableLabelFromNumber(tableNumber),
      notes: "",
    };

    persistSessionId(nextSessionId);
    syncExistingSessionQueryParams(nextSessionId, code);

    if (writeTimerRef.current) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const now = Date.now();
    sessionIdRef.current = nextSessionId;
    createdAtRef.current = null;
    hydratedRef.current = false;
    lastWriteAtRef.current = now;
    localItemsMutationAtRef.current = now;
    applyingRemoteRef.current = true;

    setActiveSessionId(nextSessionId);
    setItems([]);
    setHydrated(false);
    setCartStatus(CART_STATUSES.CART);
    setInvalidCartItems({});
    setCartAvailabilityError("");
    setCustomerInfo(null);
    setPaymentInfo(null);
    setRistaInfo(null);
    setOrderContext(nextOrderContext);
    persistLocal([], code);

    await writeCartDocument({
      cartId: cartDocId(nextSessionId, code),
      cafeCode: code,
      channel: getRistaChannelForOrderType(nextOrderContext.orderType),
      status: CART_STATUSES.CART,
      items: [],
      totals: EMPTY_TOTALS,
      orderType: nextOrderContext.orderType,
      tableNumber: nextOrderContext.tableNumber,
      tableLabel: nextOrderContext.tableLabel,
      notes: nextOrderContext.notes,
    });
  }, [orderContext, persistLocal, writeCartDocument]);

  const getProductCartQty = useCallback((product) => {
    if (!product) return 0;
    const key = product?.id || product?.skuCode || product?.name;
    return items
      .filter((it) => it.productId === key)
      .reduce((sum, it) => sum + it.quantity, 0);
  }, [items]);

  const getCartQtyForSelection = useCallback(
    (product, variant = null, selectedOptions = []) => {
      if (!product) return 0;
      const id = createCartItemId(product, variant, selectedOptions);
      const match = items.find((it) => it.id === id);
      return match ? match.quantity : 0;
    },
    [items]
  );

  const decreaseFromCart = useCallback((product) => {
    if (!product) return;
    markLocalItemsMutation();
    resetCheckoutStateForCartEdit();
    const key = product?.id || product?.skuCode || product?.name;
    setItems((prev) => {
      const candidate =
        prev.find((it) => it.productId === key && !it.variantTitle) ||
        prev.find((it) => it.productId === key);
      if (!candidate) return prev;
      return prev
        .map((it) => (it.id === candidate.id ? { ...it, quantity: it.quantity - 1 } : it))
        .filter((it) => it.quantity > 0);
    });
  }, [markLocalItemsMutation, resetCheckoutStateForCartEdit]);

  const adjustCartSelectionQuantity = useCallback(
    (product, variant = null, selectedOptions = [], delta = 1) => {
      if (!product || delta === 0) return;
      if (delta > 0) {
        addToCart(product, variant, selectedOptions);
        return;
      }
      markLocalItemsMutation();
      resetCheckoutStateForCartEdit();
      const id = createCartItemId(product, variant, selectedOptions);
      setItems((prev) =>
        prev
          .map((it) => (it.id === id ? { ...it, quantity: it.quantity - 1 } : it))
          .filter((it) => it.quantity > 0)
      );
    },
    [addToCart, markLocalItemsMutation, resetCheckoutStateForCartEdit]
  );

  // ---- derived totals -----------------------------------------------------

  const getItemTaxRatePercent = useCallback(
    (item) =>
      (item.taxTypeIds || [])
        .map((id) => {
          const taxType = taxRatesById[id];
          return Number(
            typeof taxType === "object" && taxType !== null
              ? taxType.percentage
              : taxType || 0
          );
        })
        .filter((v) => Number.isFinite(v))
        .reduce((sum, v) => sum + v, 0),
    [taxRatesById]
  );

  const getItemTaxAmount = useCallback(
    (item) => {
      const taxable = item.unitPrice * item.quantity;
      const rate = getItemTaxRatePercent(item);
      if (rate <= 0) return 0;
      if (item.isPriceIncludesTax) {
        return taxable - taxable / (1 + rate / 100);
      }
      return taxable * (rate / 100);
    },
    [getItemTaxRatePercent]
  );

  const getItemTaxBreakdown = useCallback(
    (item) => {
      const amount = roundMoney(getItemTaxAmount(item));
      const taxableAmount = roundMoney(item.unitPrice * item.quantity);
      const taxTypes = (item.taxTypeIds || [])
        .map((id) => {
          const taxType = taxRatesById[id];
          if (typeof taxType === "object" && taxType !== null) {
            return {
              name: taxType.name || taxType.title || "Tax",
              percentage: Number(taxType.percentage || 0),
            };
          }
          return {
            name: "Tax",
            percentage: Number(taxType || 0),
          };
        })
        .filter((taxType) => Number.isFinite(taxType.percentage) && taxType.percentage > 0);
      const totalRate = taxTypes.reduce((sum, taxType) => sum + taxType.percentage, 0);
      const taxes =
        amount > 0 && totalRate > 0
          ? taxTypes.map((taxType) => {
              const taxAmount = roundMoney(amount * (taxType.percentage / totalRate));
              return item.isPriceIncludesTax
                ? {
                    name: taxType.name,
                    percentage: taxType.percentage,
                    saleAmount: taxableAmount,
                    amountIncluded: taxAmount,
                    amount: taxAmount,
                  }
                : {
                    name: taxType.name,
                    percentage: taxType.percentage,
                    saleAmount: taxableAmount,
                    amountExcluded: taxAmount,
                    amount: taxAmount,
                  };
            })
          : [];
      return item.isPriceIncludesTax
        ? { taxAmountIncluded: amount, taxAmountExcluded: 0, taxAmount: amount, taxes }
        : { taxAmountIncluded: 0, taxAmountExcluded: amount, taxAmount: amount, taxes };
    },
    [getItemTaxAmount, taxRatesById]
  );

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
    [items]
  );
  const taxTotal = useMemo(
    () => items.reduce((sum, it) => sum + getItemTaxAmount(it), 0),
    [items, getItemTaxAmount]
  );
  const grandTotal = subtotal + taxTotal;
  const itemCount = items.reduce((sum, it) => sum + it.quantity, 0);

  const totals = useMemo(() => {
    const taxAmountIncluded = items.reduce(
      (sum, item) => sum + getItemTaxBreakdown(item).taxAmountIncluded,
      0
    );
    const taxAmountExcluded = items.reduce(
      (sum, item) => sum + getItemTaxBreakdown(item).taxAmountExcluded,
      0
    );
    return {
      subtotal: roundMoney(subtotal),
      taxTotal: roundMoney(taxTotal),
      taxAmountIncluded: roundMoney(taxAmountIncluded),
      taxAmountExcluded: roundMoney(taxAmountExcluded),
      grandTotal: roundMoney(grandTotal),
      itemCount,
    };
  }, [items, subtotal, taxTotal, grandTotal, itemCount, getItemTaxBreakdown]);

  const itemsWithTaxBreakdown = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        ...getItemTaxBreakdown(item),
      })),
    [items, getItemTaxBreakdown]
  );

  const reconcileBackendOrderStatus = useCallback(
    async (order) => {
      const code = cafeCodeRef.current;
      const backendStatus = order?.rista?.status;
      if (!code || !backendStatus || !isFinalCartStatus(backendStatus)) return;

      const cartId = order.cartId || cartDocId(sessionIdRef.current, code);
      const nextPayment = {
        ...(paymentInfo || {}),
        ...(order.payment || {}),
        backendOrderId: order.id || order.payment?.backendOrderId || paymentInfo?.backendOrderId || "",
      };
      const nextRista = {
        ...(ristaInfo || {}),
        ...(order.rista || {}),
      };
      const nextCustomer = customerInfo || order.customer || null;
      const nextOrderContext = {
        orderType: order.customer?.orderType || orderContext.orderType || "dine-in",
        tableNumber: String(
          order.customer?.tableNumber || orderContext.tableNumber || tableNumberRef.current || ""
        ).trim(),
        tableLabel: tableLabelFromNumber(
          order.customer?.tableNumber || orderContext.tableNumber || tableNumberRef.current
        ),
        notes: order.customer?.notes || orderContext.notes || "",
      };

      setCartStatus(backendStatus);
      setPaymentInfo(nextPayment);
      setRistaInfo(nextRista);
      setCustomerInfo(nextCustomer);
      setOrderContext(nextOrderContext);

      await writeCartDocument({
        cartId,
        cafeCode: code,
        channel: getRistaChannelForOrderType(nextOrderContext.orderType),
        status: backendStatus,
        items: itemsWithTaxBreakdown,
        totals,
        customer: nextCustomer,
        orderType: nextOrderContext.orderType,
        tableNumber: nextOrderContext.tableNumber,
        tableLabel: nextOrderContext.tableLabel,
        notes: nextOrderContext.notes,
        payment: nextPayment,
        rista: nextRista,
      });
    },
    [
      customerInfo,
      itemsWithTaxBreakdown,
      orderContext,
      paymentInfo,
      ristaInfo,
      totals,
      writeCartDocument,
    ]
  );

  // Write-through: whenever items change (from a local action), debounce-sync
  // to Firestore + localStorage. Remote-applied changes are skipped here.
  useEffect(() => {
    if (!hydrated) return;
    if (applyingRemoteRef.current) {
      applyingRemoteRef.current = false;
      return;
    }
    const code = cafeCodeRef.current;
    const cartId = cartDocId(sessionIdRef.current, code);
    const nextOrderContext = {
      ...orderContext,
      tableLabel:
        orderContext.tableLabel ||
        tableLabelFromNumber(orderContext.tableNumber || tableNumberRef.current),
    };
    persistLocal(items, code);

    if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => {
      writeCartDocument({
        cartId,
        cafeCode: code,
        channel: getRistaChannelForOrderType(nextOrderContext.orderType),
        status: cartStatus || CART_STATUSES.CART,
        items: itemsWithTaxBreakdown,
        totals,
        customer: customerInfo,
        orderType: nextOrderContext.orderType,
        tableNumber: nextOrderContext.tableNumber,
        tableLabel: nextOrderContext.tableLabel,
        notes: nextOrderContext.notes,
        payment: paymentInfo,
        rista: ristaInfo,
      });
    }, 500);

    return () => {
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    };
  }, [
    items,
    itemsWithTaxBreakdown,
    hydrated,
    persistLocal,
    writeCartDocument,
    totals,
    cartStatus,
    customerInfo,
    orderContext,
    paymentInfo,
    ristaInfo,
  ]);

  // ---- checkout / place order --------------------------------------------

  const saveCartSessionForCheckout = useCallback(
    async ({ status, customer, payment, rista }) => {
      const code = cafeCodeRef.current;
      const cartId = cartDocId(sessionIdRef.current, code);
      const orderType = customer?.orderType || "dine-in";
      const tableNumber = String(customer?.tableNumber || tableNumberRef.current || "").trim();
      const tableLabel = customer?.tableLabel || tableLabelFromNumber(tableNumber);
      const nextOrderContext = {
        orderType,
        tableNumber,
        tableLabel,
        notes: customer?.notes || "",
      };

      let resolved = {
        status,
        customer: customer || null,
        payment: payment || null,
        rista: rista || null,
        orderContext: nextOrderContext,
      };

      try {
        const updatedAt = Date.now();
        const docRef = firestore.collection("menu_carts").doc(cartId);
        lastWriteAtRef.current = updatedAt;

        resolved = await firestore.runTransaction(async (transaction) => {
          const currentSnap = await transaction.get(docRef);
          const currentDoc = currentSnap.exists ? currentSnap.data() || {} : {};
          const currentStatus = currentDoc.status || currentDoc.rista?.status || "";
          const shouldPreserveFinalStatus =
            isFinalCartStatus(currentStatus) && !isFinalCartStatus(status);
          const resolvedStatus = shouldPreserveFinalStatus
            ? currentDoc.status || currentStatus
            : status;
          const resolvedRista = shouldPreserveFinalStatus ? currentDoc.rista : rista;
          const resolvedPayment = shouldPreserveFinalStatus
            ? currentDoc.payment || payment
            : payment;
          const resolvedCustomer = shouldPreserveFinalStatus
            ? currentDoc.customer || customer
            : customer;
          const resolvedOrderContext = shouldPreserveFinalStatus
            ? {
                orderType: currentDoc.orderType || nextOrderContext.orderType,
                tableNumber:
                  currentDoc.tableNumber !== undefined
                    ? currentDoc.tableNumber || ""
                    : nextOrderContext.tableNumber,
                tableLabel:
                  currentDoc.tableLabel !== undefined
                    ? currentDoc.tableLabel ||
                      tableLabelFromNumber(currentDoc.tableNumber || tableNumberRef.current)
                    : nextOrderContext.tableLabel,
                notes:
                  currentDoc.notes !== undefined
                    ? currentDoc.notes || ""
                    : nextOrderContext.notes,
              }
            : nextOrderContext;
          const createdAt = currentDoc.createdAt || createdAtRef.current || updatedAt;

          transaction.set(
            docRef,
            stripUndefined({
              cartId,
              sessionId: sessionIdRef.current,
              cafeCode: code,
              channel: getRistaChannelForOrderType(resolvedOrderContext.orderType),
              status: resolvedStatus,
              items: itemsWithTaxBreakdown,
              totals,
              customer: resolvedCustomer,
              orderType: resolvedOrderContext.orderType,
              tableNumber: resolvedOrderContext.tableNumber,
              tableLabel: resolvedOrderContext.tableLabel,
              notes: resolvedOrderContext.notes,
              payment: resolvedPayment,
              rista: resolvedRista,
              createdAt,
              updatedAt,
              updatedAtClient: new Date().toISOString(),
              expiresAt:
                currentDoc.expiresAt ||
                new Date(Date.now() + CART_TTL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
            }),
            { merge: true }
          );

          return {
            status: resolvedStatus,
            customer: resolvedCustomer || null,
            payment: resolvedPayment || null,
            rista: resolvedRista || null,
            orderContext: resolvedOrderContext,
            createdAt,
          };
        });

        createdAtRef.current = resolved.createdAt || createdAtRef.current;
        setSyncError(false);
      } catch (error) {
        console.warn("[cart] Checkout session write failed, using local state only:", error);
        setSyncError(true);
      }

      setCartStatus(resolved.status);
      setCustomerInfo(resolved.customer || null);
      setPaymentInfo(resolved.payment || null);
      setRistaInfo(resolved.rista || null);
      setOrderContext(resolved.orderContext);
    },
    [itemsWithTaxBreakdown, totals]
  );

  const validateCartAvailability = useCallback(async () => {
    const code = cafeCodeRef.current;
    const unavailableMessage =
      "Some items in your cart are now sold out. Please remove them to continue.";
    const unavailableCheckMessage =
      "Could not confirm item availability. Please try again before payment.";

    if (!code || items.length === 0) {
      return { valid: false, code: AVAILABILITY_ERROR_CODES.AVAILABILITY_CHECK_FAILED, error: "Cart is empty" };
    }

    try {
      const soldOutMap = await fetchSoldOutItems(code, { strict: true });
      const invalidItems = findInvalidCartItems(items, soldOutMap);

      if (hasInvalidCartItems(invalidItems)) {
        setInvalidCartItems(invalidItems);
        setCartAvailabilityError(unavailableMessage);
        return {
          valid: false,
          code: AVAILABILITY_ERROR_CODES.SOLD_OUT_ITEMS,
          error: unavailableMessage,
          invalidItems,
        };
      }

      setInvalidCartItems({});
      setCartAvailabilityError("");
      return { valid: true, invalidItems: {} };
    } catch (error) {
      console.warn("[cart] availability check failed:", error);
      setCartAvailabilityError(unavailableCheckMessage);
      return {
        valid: false,
        code: AVAILABILITY_ERROR_CODES.AVAILABILITY_CHECK_FAILED,
        error: unavailableCheckMessage,
      };
    }
  }, [items]);

  /**
   * Place an order: create a Razorpay order on the backend, open the checkout
   * modal, verify the payment, and keep the cart session alive for webhook
   * driven Rista status updates.
   *
   * @param {{ name:string, phone:string, orderType:string, tableNumber?:string, notes?:string }} customer
   * @returns {Promise<{ success:boolean, orderId?:string, status?:string, error?:string, dismissed?:boolean }>}
   */
  const placeOrder = useCallback(
    async (customer) => {
      const code = cafeCodeRef.current;
      if (!code || items.length === 0) {
        return { success: false, error: "Cart is empty" };
      }

      const availability = await validateCartAvailability();
      if (!availability.valid) {
        return {
          success: false,
          code: availability.code,
          error: availability.error,
          invalidItems: availability.invalidItems || {},
        };
      }

      const sessionId = sessionIdRef.current;
      const cartId = cartDocId(sessionId, code);
      const amountPaise = Math.round(grandTotal * 100);
      const orderRef = `MENU-${code}-${Date.now()}`;
      const orderType = customer?.orderType || "dine-in";
      const tableNumber = String(customer?.tableNumber || tableNumberRef.current || "").trim();
      const tableLabel = tableLabelFromNumber(tableNumber);
      const orderLabel = buildRistaQrOrderLabel({ orderType, tableNumber });
      const checkoutCustomer = {
        ...customer,
        orderType,
        tableNumber,
        tableLabel,
      };

      const basePayment = {
        orderRef,
        amountPaise,
        currency: "INR",
      };
      const razorpayNotes = stripUndefined({
        order_ref: orderRef,
        session_id: sessionId,
        cart_id: cartId,
        cafe_code: code,
        table_label: tableLabel || undefined,
        order_label: orderLabel || undefined,
        orderLabel: orderLabel || undefined,
      });
      const initialSaleDraft = buildRistaSaleDraft({
        cafeCode: code,
        cartId,
        orderRef,
        items: itemsWithTaxBreakdown,
        totals,
        customer: checkoutCustomer,
        orderType: checkoutCustomer.orderType,
        tableNumber,
        tableLabel,
        payment: basePayment,
      });

      const payload = {
        orderRef,
        sessionId,
        cartId,
        cafeCode: code,
        branchName: cafeInfo.branchName || "",
        amountPaise,
        customer: checkoutCustomer,
        items: items.map((it) => ({
          id: it.id,
          itemId: it.itemId,
          skuCode: it.skuCode,
          name: it.name,
          shortName: it.shortName,
          longName: it.longName,
          categoryName: it.categoryName,
          subCategoryName: it.subCategoryName,
          baseUnitPrice: it.baseUnitPrice,
          unitPrice: it.unitPrice,
          quantity: it.quantity,
          selectedOptions: it.selectedOptions,
          taxTypeIds: it.taxTypeIds,
          isPriceIncludesTax: it.isPriceIncludesTax,
        })),
        totals,
        notes: razorpayNotes,
        rista: { saleDraft: initialSaleDraft },
      };

      try {
        const order = await createOrderOnBackend(payload);
        const paymentStarted = {
          ...basePayment,
          backendOrderId: order.orderId,
          razorpayOrderId: order.razorpayOrderId,
        };

        await saveCartSessionForCheckout({
          status: CART_STATUSES.PAYMENT_STARTED,
          customer: checkoutCustomer,
          payment: paymentStarted,
          rista: { saleDraft: initialSaleDraft },
        });

        await loadRazorpayScript();

        const rzpResponse = await openRazorpayCheckout({
          amountPaise: order.amount || amountPaise,
          keyId: order.keyId,
          orderId: order.razorpayOrderId,
          customerName: checkoutCustomer?.name,
          customerPhone: checkoutCustomer?.phone,
          orderRef,
          notes: razorpayNotes,
        }).catch((err) => {
          if (err?.message === "RAZORPAY_MODAL_DISMISSED") {
            return { dismissed: true };
          }
          throw err;
        });

        if (rzpResponse?.dismissed) {
          return { success: false, dismissed: true };
        }

        const verifiedPayment = {
          ...paymentStarted,
          razorpayPaymentId: rzpResponse.razorpay_payment_id,
          razorpaySignature: rzpResponse.razorpay_signature,
        };

        let verify = null;
        try {
          verify = await verifyPaymentOnBackend({
            orderId: order.orderId,
            razorpayOrderId: rzpResponse.razorpay_order_id,
            razorpayPaymentId: rzpResponse.razorpay_payment_id,
            razorpaySignature: rzpResponse.razorpay_signature,
          });
        } catch (verifyError) {
          console.warn("[cart] payment verify request failed; waiting for webhook:", verifyError);
          verify = {
            verified: true,
            status: CART_STATUSES.RISTA_PENDING,
            webhookPending: true,
          };
        }

        if (verify.verified) {
          const verifiedSaleDraft = buildRistaSaleDraft({
            cafeCode: code,
            cartId,
            orderRef,
            items: itemsWithTaxBreakdown,
            totals,
            customer: checkoutCustomer,
            orderType: checkoutCustomer.orderType,
            tableNumber,
            tableLabel,
            payment: verifiedPayment,
          });

          await saveCartSessionForCheckout({
            status: CART_STATUSES.RISTA_PENDING,
            customer: checkoutCustomer,
            payment: verifiedPayment,
            rista: {
              saleDraft: verifiedSaleDraft,
              status: CART_STATUSES.RISTA_PENDING,
            },
          });

          return {
            success: true,
            orderId: order.orderId,
            status: verify.status || CART_STATUSES.PAYMENT_VERIFIED,
          };
        }

        await saveCartSessionForCheckout({
          status: CART_STATUSES.PAYMENT_FAILED,
          customer: checkoutCustomer,
          payment: verifiedPayment,
          rista: {
            saleDraft: initialSaleDraft,
            error: "Payment verification failed",
          },
        });

        return {
          success: false,
          orderId: order.orderId,
          status: verify.status,
          error: "Payment verification failed",
        };
      } catch (error) {
        console.error("[cart] placeOrder failed:", error);

        if (error?.code === AVAILABILITY_ERROR_CODES.SOLD_OUT_ITEMS) {
          const invalidItems = error?.data?.invalidItems || {};
          setInvalidCartItems(invalidItems);
          setCartAvailabilityError(
            error?.message || "Some items in your cart are now sold out. Please remove them to continue."
          );
          return {
            success: false,
            code: AVAILABILITY_ERROR_CODES.SOLD_OUT_ITEMS,
            error: error?.message || "Some items in your cart are now sold out.",
            invalidItems,
          };
        }

        if (error?.code === AVAILABILITY_ERROR_CODES.AVAILABILITY_CHECK_FAILED) {
          setCartAvailabilityError(
            error?.message || "Could not confirm item availability. Please try again before payment."
          );
          return {
            success: false,
            code: AVAILABILITY_ERROR_CODES.AVAILABILITY_CHECK_FAILED,
            error: error?.message || "Could not confirm item availability. Please try again before payment.",
          };
        }

        await saveCartSessionForCheckout({
          status: CART_STATUSES.PAYMENT_FAILED,
          customer: checkoutCustomer,
          payment: basePayment,
          rista: {
            saleDraft: initialSaleDraft,
            error: error?.message || "Order failed",
          },
        });
        return { success: false, error: error?.message || "Order failed" };
      }
    },
    [
      items,
      grandTotal,
      totals,
      itemsWithTaxBreakdown,
      cafeInfo,
      saveCartSessionForCheckout,
      validateCartAvailability,
    ]
  );

  const value = useMemo(
    () => ({
      items,
      hydrated,
      syncError,
      cafeInfo,
      sessionId: activeSessionId,
      cartId: cartDocId(activeSessionId, cafeCodeRef.current),
      cartStatus,
      invalidCartItems,
      cartAvailabilityError,
      customerInfo,
      paymentInfo,
      ristaInfo,
      orderContext,
      tableNumber: orderContext.tableNumber,
      tableLabel: orderContext.tableLabel,
      // mutations
      addToCart,
      updateQuantity,
      replaceCartItem,
      removeItem,
      clearCart,
      startNewCartAfterSuccessfulOrder,
      reconcileBackendOrderStatus,
      decreaseFromCart,
      adjustCartSelectionQuantity,
      // queries
      getProductCartQty,
      getCartQtyForSelection,
      createCartItemId,
      // tax / cafe info setters (used by the menu page)
      setTaxRates,
      setCafeDetails,
      setOrderNotes,
      clearCartAvailabilityError,
      // derived totals
      itemCount,
      subtotal,
      taxTotal,
      grandTotal,
      // checkout
      placeOrder,
    }),
    [
      items,
      hydrated,
      syncError,
      cafeInfo,
      activeSessionId,
      cartStatus,
      invalidCartItems,
      cartAvailabilityError,
      customerInfo,
      paymentInfo,
      ristaInfo,
      orderContext,
      addToCart,
      updateQuantity,
      replaceCartItem,
      removeItem,
      clearCart,
      startNewCartAfterSuccessfulOrder,
      reconcileBackendOrderStatus,
      decreaseFromCart,
      adjustCartSelectionQuantity,
      getProductCartQty,
      getCartQtyForSelection,
      setTaxRates,
      setCafeDetails,
      setOrderNotes,
      clearCartAvailabilityError,
      itemCount,
      subtotal,
      taxTotal,
      grandTotal,
      placeOrder,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
